import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircle as AddCircleIcon,
  CalendarMonth as CalendarMonthIcon,
  CreditCard as CreditCardIcon,
  Event as EventIcon,
  FactCheck as FactCheckIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { useTenant } from '../../providers/TenantProvider';
import type {
  Appointment,
  CheckIn,
  Invoice,
  InvoiceStatus,
  Member,
  MemberStatus,
  Plan,
  Subscription,
  SubscriptionStatus,
} from '../../lib/db/types';

interface MemberWithId extends Member {
  id: string;
}

interface SubscriptionWithId extends Subscription {
  id: string;
}

interface InvoiceWithId extends Invoice {
  id: string;
}

interface CheckInWithId extends CheckIn {
  id: string;
}

interface AppointmentWithId extends Appointment {
  id: string;
}

interface PlanWithId extends Plan {
  id: string;
  stripePriceId?: string;
}

const ACTIVE_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];

function timestampToDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === 'object') {
    const timestamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
  }

  return null;
}

function formatDate(value: unknown, options?: Intl.DateTimeFormatOptions): string {
  const date = timestampToDate(value);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', options);
}

function formatDateTime(value: unknown): string {
  const date = timestampToDate(value);
  if (!date) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(amount?: number, currency = 'USD'): string {
  if (typeof amount !== 'number') return 'N/A';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function getStatusColor(status: MemberStatus | SubscriptionStatus | InvoiceStatus) {
  switch (status) {
    case 'active':
    case 'paid':
    case 'trialing':
      return 'success';
    case 'paused':
    case 'past_due':
    case 'incomplete':
    case 'open':
    case 'draft':
      return 'warning';
    case 'canceled':
    case 'unpaid':
    case 'incomplete_expired':
    case 'uncollectible':
    case 'void':
      return 'error';
    default:
      return 'default';
  }
}

function getPlanPriceId(plan: PlanWithId): string | undefined {
  return plan.stripePriceId || plan.priceIds?.[0]?.id;
}

function byTimestampDesc<T>(items: T[], getValue: (item: T) => unknown): T[] {
  return [...items].sort((left, right) => {
    const leftTime = timestampToDate(getValue(left))?.getTime() || 0;
    const rightTime = timestampToDate(getValue(right))?.getTime() || 0;
    return rightTime - leftTime;
  });
}

function byTimestampAsc<T>(items: T[], getValue: (item: T) => unknown): T[] {
  return [...items].sort((left, right) => {
    const leftTime = timestampToDate(getValue(left))?.getTime() || 0;
    const rightTime = timestampToDate(getValue(right))?.getTime() || 0;
    return leftTime - rightTime;
  });
}

export function MemberDashboardPage() {
  const navigate = useNavigate();
  const { user, tenantId, activeMembership } = useTenant();
  const [member, setMember] = useState<MemberWithId | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithId[]>([]);
  const [invoices, setInvoices] = useState<InvoiceWithId[]>([]);
  const [checkIns, setCheckIns] = useState<CheckInWithId[]>([]);
  const [appointments, setAppointments] = useState<AppointmentWithId[]>([]);
  const [plans, setPlans] = useState<PlanWithId[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !tenantId) {
      setError('Please sign in to view your member portal.');
      setLoading(false);
      return;
    }

    if (!activeMembership?.memberId) {
      setError('Member record not found. Please contact staff.');
      setLoading(false);
      return;
    }

    const currentMemberId = activeMembership.memberId;
    let cancelled = false;

    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        const memberSnap = await getDoc(doc(db, 'members', currentMemberId));
        if (!memberSnap.exists()) {
          throw new Error('Member record not found. Please contact staff.');
        }

        const memberData = { id: memberSnap.id, ...memberSnap.data() } as MemberWithId;
        if (memberData.tenantId !== tenantId) {
          throw new Error('Member record does not match the active tenant.');
        }

        const subscriptionsQuery = query(
          collection(db, 'subscriptions'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', currentMemberId)
        );
        const invoicesQuery = query(
          collection(db, 'invoices'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', currentMemberId)
        );
        const checkInsQuery = query(
          collection(db, 'checkins'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', currentMemberId)
        );
        const appointmentsQuery = query(
          collection(db, 'appointments'),
          where('tenantId', '==', tenantId),
          where('customerType', '==', 'member'),
          where('customerId', '==', currentMemberId)
        );
        const plansQuery = query(
          collection(db, 'plans'),
          where('tenantId', '==', tenantId),
          where('active', '==', true)
        );

        const [
          subscriptionsSnap,
          invoicesSnap,
          checkInsSnap,
          appointmentsSnap,
          plansSnap,
        ] = await Promise.all([
          getDocs(subscriptionsQuery),
          getDocs(invoicesQuery),
          getDocs(checkInsQuery),
          getDocs(appointmentsQuery),
          getDocs(plansQuery),
        ]);

        if (cancelled) return;

        setMember(memberData);
        setSubscriptions(
          byTimestampDesc(
            subscriptionsSnap.docs.map((document) => ({
              id: document.id,
              ...document.data(),
            })) as SubscriptionWithId[],
            (subscription) => subscription.currentPeriodEnd
          )
        );
        setInvoices(
          byTimestampDesc(
            invoicesSnap.docs.map((document) => ({
              id: document.id,
              ...document.data(),
            })) as InvoiceWithId[],
            (invoice) => invoice.paidAt || invoice.dueDate || invoice.createdAt
          )
        );
        setCheckIns(
          byTimestampDesc(
            checkInsSnap.docs.map((document) => ({
              id: document.id,
              ...document.data(),
            })) as CheckInWithId[],
            (checkIn) => checkIn.timestamp
          )
        );
        setAppointments(
          byTimestampAsc(
            appointmentsSnap.docs.map((document) => ({
              id: document.id,
              ...document.data(),
            })) as AppointmentWithId[],
            (appointment) => appointment.startTime
          )
        );
        setPlans(
          plansSnap.docs.map((document) => ({
            id: document.id,
            ...document.data(),
          })) as PlanWithId[]
        );
      } catch (err) {
        console.error('Error loading member dashboard:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load member portal.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [activeMembership?.memberId, tenantId, user]);

  useEffect(() => {
    if (selectedPlanId || plans.length === 0) return;
    const firstBillablePlan = plans.find((plan) => !!getPlanPriceId(plan)) || plans[0];
    setSelectedPlanId(firstBillablePlan.id);
  }, [plans, selectedPlanId]);

  const activeSubscription = useMemo(() => {
    if (!member) return null;
    return (
      subscriptions.find(
        (subscription) =>
          subscription.id === member.currentSubscriptionId ||
          subscription.stripeSubscriptionId === member.currentSubscriptionId
      ) ||
      subscriptions.find((subscription) => ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)) ||
      subscriptions[0] ||
      null
    );
  }, [member, subscriptions]);

  const upcomingAppointments = useMemo(
    () =>
      appointments.filter((appointment) => {
        const startTime = timestampToDate(appointment.startTime);
        return Boolean(startTime && startTime.getTime() > Date.now() && appointment.status !== 'canceled');
      }),
    [appointments]
  );

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) || null;
  const selectedPlanPriceId = selectedPlan ? getPlanPriceId(selectedPlan) : undefined;
  const hasBillablePlans = plans.some((plan) => !!getPlanPriceId(plan));

  const handleManageBilling = async () => {
    if (!member) return;

    try {
      setBillingLoading(true);
      setError(null);

      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortalSession({
        memberId: member.id,
        returnUrl: `${window.location.origin}/member`,
      });
      const data = result.data as { success?: boolean; sessionUrl?: string; portalUrl?: string };
      const portalUrl = data.sessionUrl || data.portalUrl;
      if (!portalUrl) {
        throw new Error('Billing portal URL was not returned.');
      }
      window.location.assign(portalUrl);
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal.');
      setBillingLoading(false);
    }
  };

  const handleStartSubscription = async () => {
    if (!member || !selectedPlanPriceId) return;

    try {
      setCheckoutLoading(true);
      setError(null);

      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        memberId: member.id,
        priceId: selectedPlanPriceId,
        successUrl: `${window.location.origin}/member?checkout=success`,
        cancelUrl: `${window.location.origin}/member?checkout=cancel`,
      });
      const data = result.data as { success?: boolean; sessionUrl?: string };
      if (!data.sessionUrl) {
        throw new Error('Checkout URL was not returned.');
      }
      window.location.assign(data.sessionUrl);
    } catch (err) {
      console.error('Error starting subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to start subscription checkout.');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !member) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 3, md: 4 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              Hi, {member?.contact.firstName || 'there'}
            </Typography>
            <Typography color="text.secondary">
              Your membership, visits, billing, and appointments.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/member/book')}
          >
            Book a Service
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <QrCodeIcon color="primary" />
                  <Typography variant="h6">Check-In Pass</Typography>
                </Stack>
                {member?.barcodeId ? (
                  <Stack alignItems="center" spacing={2}>
                    <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <QRCodeSVG value={member.barcodeId} size={180} level="H" />
                    </Box>
                    <Typography variant="h6" fontFamily="monospace" align="center">
                      {member.barcodeId}
                    </Typography>
                    <Chip label={`${member.attendanceCount || 0} check-ins`} color="primary" variant="outlined" />
                  </Stack>
                ) : (
                  <Alert severity="warning">
                    Your check-in barcode is not assigned yet. Please contact staff.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6">Member Data</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography>
                      {member?.contact.firstName} {member?.contact.lastName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography>{member?.contact.email}</Typography>
                  </Box>
                  {member?.contact.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography>{member.contact.phone}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={member?.status || 'unknown'}
                        color={member ? getStatusColor(member.status) : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Joined</Typography>
                    <Typography>
                      {formatDate(member?.joinedAt, { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <CreditCardIcon color="primary" />
                  <Typography variant="h6">Subscription</Typography>
                </Stack>
                {activeSubscription ? (
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={activeSubscription.status}
                          color={getStatusColor(activeSubscription.status)}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current Period</Typography>
                      <Typography>
                        {formatDate(activeSubscription.currentPeriodStart)} - {formatDate(activeSubscription.currentPeriodEnd)}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<CreditCardIcon />}
                      onClick={handleManageBilling}
                      disabled={billingLoading}
                    >
                      {billingLoading ? 'Opening...' : 'Manage Billing'}
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography color="text.secondary">
                      No active subscription found.
                    </Typography>
                    {plans.length > 0 ? (
                      <>
                        <TextField
                          select
                          size="small"
                          label="Plan"
                          value={selectedPlanId}
                          onChange={(event) => setSelectedPlanId(event.target.value)}
                          fullWidth
                        >
                          {plans.map((plan) => (
                            <MenuItem key={plan.id} value={plan.id} disabled={!getPlanPriceId(plan)}>
                              {plan.name} - {formatCurrency(plan.price, plan.priceIds?.[0]?.currency)}
                              {!getPlanPriceId(plan) ? ' (billing unavailable)' : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                        {hasBillablePlans ? (
                          <Button
                            variant="contained"
                            startIcon={<CreditCardIcon />}
                            onClick={handleStartSubscription}
                            disabled={!selectedPlanPriceId || checkoutLoading}
                          >
                            {checkoutLoading ? 'Starting...' : 'Start Subscription'}
                          </Button>
                        ) : (
                          <Alert severity="info">
                            Online billing is not enabled for this gym yet. Contact staff to start this plan.
                          </Alert>
                        )}
                      </>
                    ) : (
                      <Alert severity="info">No membership plans are currently available.</Alert>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <EventIcon color="primary" />
                  <Typography variant="h6">Upcoming Appointments</Typography>
                </Stack>
                {upcomingAppointments.length === 0 ? (
                  <Stack spacing={2}>
                    <Typography color="text.secondary">No upcoming appointments.</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddCircleIcon />}
                      onClick={() => navigate('/member/book')}
                    >
                      Book a Service
                    </Button>
                  </Stack>
                ) : (
                  <List disablePadding>
                    {upcomingAppointments.slice(0, 3).map((appointment, index) => (
                      <Box key={appointment.id}>
                        {index > 0 && <Divider />}
                        <ListItem disableGutters>
                          <ListItemText
                            primary={formatDateTime(appointment.startTime)}
                            secondary={`Status: ${appointment.status}`}
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                )}
                <Button
                  sx={{ mt: 2 }}
                  variant="text"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => navigate('/member/appointments')}
                >
                  View Appointments
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6">Check-In History</Typography>
                </Stack>
                {checkIns.length === 0 ? (
                  <Typography color="text.secondary">No check-ins yet.</Typography>
                ) : (
                  <List disablePadding>
                    {checkIns.slice(0, 5).map((checkIn, index) => (
                      <Box key={checkIn.id}>
                        {index > 0 && <Divider />}
                        <ListItem disableGutters>
                          <ListItemText
                            primary={formatDateTime(checkIn.timestamp)}
                            secondary={`Method: ${checkIn.method}`}
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <FactCheckIcon color="primary" />
                  <Typography variant="h6">Recent Invoices</Typography>
                </Stack>
                {invoices.length === 0 ? (
                  <Typography color="text.secondary">No invoices found.</Typography>
                ) : (
                  <List disablePadding>
                    {invoices.slice(0, 5).map((invoice, index) => (
                      <Box key={invoice.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          disableGutters
                          secondaryAction={
                            <Chip
                              label={invoice.status}
                              color={getStatusColor(invoice.status)}
                              size="small"
                            />
                          }
                        >
                          <ListItemText
                            primary={formatCurrency(invoice.total, invoice.currency)}
                            secondary={
                              invoice.paidAt
                                ? `Paid ${formatDate(invoice.paidAt)}`
                                : `Due ${formatDate(invoice.dueDate)}`
                            }
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default MemberDashboardPage;
